import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ClipboardCheck, Loader2, ChevronLeft, Plus, CheckCircle2, XCircle,
  AlertTriangle, ClipboardList, Clock, ShieldCheck, RefreshCw, Printer, Trash2, ArchiveRestore, Pencil
} from 'lucide-react';
import { TemplatesTab } from '@/components/inlineQuality/TemplatesTab';
import { OperatorSelect } from '@/components/OperatorSelect';
import { printChecksheet } from '@/components/inlineQuality/printChecksheet';
import { hasPermission } from '@/utils/permissions';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_META = {
  open: { label: 'In Progress', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  attention: { label: 'Needs Attention', cls: 'bg-red-50 text-red-700 border-red-200' },
  lab_pending: { label: 'Verified (Lab Pending)', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  verified: { label: 'Verified', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : format(d, 'dd MMM yy');
};

const mcLabel = (i) => i.machine_name || i.machine_no || '—';

const sampleLabel = (size, frequency) => {
  const f = (frequency || '').toLowerCase();
  if (f.startsWith('hour')) return `${size} / hr`;
  return frequency ? `${size} · ${frequency}` : `${size}`;
};

const Sparkline = ({ values, low, high }) => {
  if (!values || values.length < 2) return <span className="text-slate-300 text-[10px]">—</span>;
  const w = 64, h = 24, pad = 3;
  const all = [...values];
  if (low != null) all.push(low);
  if (high != null) all.push(high);
  let min = Math.min(...all), max = Math.max(...all);
  if (min === max) { min -= 1; max += 1; }
  const span = max - min;
  min -= span * 0.1; max += span * 0.1;
  const x = (i) => pad + (i * (w - 2 * pad)) / (values.length - 1);
  const y = (v) => h - pad - ((v - min) * (h - 2 * pad)) / (max - min);
  const anyOut = values.some((v) => (low != null && v < low) || (high != null && v > high));
  const last = values[values.length - 1];
  const lastOut = (low != null && last < low) || (high != null && last > high);
  const color = anyOut ? '#dc2626' : '#059669';
  return (
    <svg width={w} height={h} className="block">
      {low != null && high != null && (
        <rect x={pad} y={y(high)} width={w - 2 * pad} height={Math.max(1, y(low) - y(high))} fill="#10b981" opacity="0.08" />
      )}
      {low != null && <line x1={pad} x2={w - pad} y1={y(low)} y2={y(low)} stroke="#94a3b8" strokeDasharray="2,2" strokeWidth="0.8" />}
      {high != null && <line x1={pad} x2={w - pad} y1={y(high)} y2={y(high)} stroke="#94a3b8" strokeDasharray="2,2" strokeWidth="0.8" />}
      <polyline points={values.map((v, i) => `${x(i)},${y(v)}`).join(' ')} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx={x(values.length - 1)} cy={y(last)} r="2.2" fill={lastOut ? '#dc2626' : color} />
    </svg>
  );
};

export default function InlineQuality() {
  const [templates, setTemplates] = useState([]);
  const [stageIdx, setStageIdx] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [machines, setMachines] = useState([]);
  const [jobId, setJobId] = useState('');
  const [shift, setShift] = useState('General A');
  const [machine, setMachine] = useState('');
  const [operator, setOperator] = useState('');
  const [operatorEmpId, setOperatorEmpId] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [tab, setTab] = useState('enter');
  const [allInsp, setAllInsp] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [olderCount, setOlderCount] = useState(0);
  const me = JSON.parse(localStorage.getItem('user') || '{}');
  const verifyAllowed = canVerify(me);
  const entryAllowed = canEnter(me);
  const templatesAllowed = canEditTemplates(me);

  const loadAll = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/inline-quality/inspections`, auth()); setAllInsp(r.data || []); } catch { /* silent */ }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/inline-quality/templates`, auth());
      setTemplates(r.data || []);
      if (r.data?.length && stageIdx === null) setStageIdx(r.data[0].stage_index);
    } catch { toast.error('Failed to load templates'); }
  }, [stageIdx]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const loadStageData = useCallback(async () => {
    if (stageIdx === null) return;
    setLoading(true);
    try {
      const [j, m] = await Promise.all([
        axios.get(`${API}/api/inline-quality/eligible-jobs/${stageIdx}`, auth()),
        axios.get(`${API}/api/inline-quality/machines/${stageIdx}`, auth()),
      ]);
      setJobs(j.data || []);
      setMachines(m.data || []);
      setMachine('');
    } catch { toast.error('Failed to load stage data'); } finally { setLoading(false); }
  }, [stageIdx]);

  useEffect(() => { loadStageData(); }, [loadStageData]);

  // Sheets already running today for the SAME (job, stage, shift) but on a DIFFERENT machine
  // → drives the soft "already running on M/c X" banner in the New Sheet form
  const otherMachineSheet = useMemo(() => {
    if (!jobId) return null;
    return allInsp.find((i) => i.job_id === jobId
      && i.stage_index === stageIdx
      && i.shift === shift
      && i.status !== 'verified'
      && (isToday(i.created_at) || isToday(i.date))
      && machine && (i.machine_no || '') !== machine
    );
  }, [allInsp, jobId, stageIdx, shift, machine]);

  const startInspection = async () => {
    if (!jobId) { toast.error('Select a job'); return; }
    // Exact-match resume: same job + stage + shift + machine + today → open it instead of creating a dupe.
    // Different machine on same job/stage/shift is now ALLOWED (Option A: multi-machine).
    const existing = allInsp.find((i) => i.job_id === jobId
      && i.stage_index === stageIdx
      && i.shift === shift
      && (i.machine_no || '') === (machine || '')
      && i.status !== 'verified'
      && (isToday(i.created_at) || isToday(i.date)));
    if (existing) {
      await openInspection(existing.id);
      setShowNewForm(false);
      toast.success('Resumed existing sheet for this job / shift / machine today');
      return;
    }
    setStarting(true);
    try {
      const r = await axios.post(`${API}/api/inline-quality/inspections`,
        { job_id: jobId, stage_index: stageIdx, shift, machine_no: machine, operator_name: operator }, auth());
      setActive(r.data);
      setShowNewForm(false);
      toast.success('Inspection started');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to start'); } finally { setStarting(false); }
  };

  const openInspection = async (id) => {
    try { const r = await axios.get(`${API}/api/inline-quality/inspections/${id}`, auth()); setActive(r.data); }
    catch { toast.error('Failed to open'); }
  };

  const tpl = templates.find((t) => t.stage_index === stageIdx);

  if (active) {
    return <ChecksheetView inspection={active} onBack={() => { setActive(null); loadStageData(); loadAll(); }}
      onRefresh={async () => { const r = await axios.get(`${API}/api/inline-quality/inspections/${active.id}`, auth()); setActive(r.data); }} />;
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5" data-testid="inline-quality-page">
      <div className="flex items-center gap-3">
        <div className="p-2.5 bg-indigo-600 rounded-xl"><ClipboardCheck className="w-6 h-6 text-white" /></div>
        <div>
          <h1 className="text-xl font-bold text-slate-800">In-line Quality</h1>
          <p className="text-sm text-slate-500">In-process validation checksheets — इन-प्रोसेस वैलिडेशन</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5" data-testid="iq-tabs">
        {[
          ['enter', 'Checksheets', ClipboardCheck],
          ...(verifyAllowed ? [['verify', `Verify${allInsp.filter((i) => i.status !== 'verified').length > 0 ? ` (${allInsp.filter((i) => i.status !== 'verified').length})` : ''}`, ShieldCheck]] : []),
          ...(templatesAllowed ? [['templates', 'Templates', Pencil]] : []),
          ...(me.role === 'Admin' ? [['archive', 'Archive', ArchiveRestore]] : []),
        ].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`iq-tab-${id}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors flex items-center gap-1.5 ${tab === id ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {tab === 'enter' && (<>
      {/* New sheet form (collapsed by default) */}
      {entryAllowed && showNewForm && (
      <Card><CardContent className="p-4 sm:p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
            <Plus className="w-4 h-4" /> Start New Inspection
            {tpl && <span className="text-xs font-normal text-slate-400 ml-1">{tpl.title_hi} · {tpl.doc_no}</span>}
          </div>
          <button onClick={() => setShowNewForm(false)} data-testid="iq-new-sheet-cancel"
            className="text-xs font-semibold text-slate-400 hover:text-slate-700">Cancel</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Stage / Process</label>
            <select value={stageIdx ?? ''} onChange={(e) => setStageIdx(Number(e.target.value))} data-testid="iq-stage-select"
              className="w-full px-3 py-3 border border-slate-300 rounded-lg text-sm">
              {templates.filter((t) => !t.archived).map((t) => <option key={t.stage_index} value={t.stage_index}>{t.stage_name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Job Card (Heat/Lot No.)</label>
            <select value={jobId} onChange={(e) => setJobId(e.target.value)} data-testid="iq-job-select"
              className="w-full px-3 py-3 border border-slate-300 rounded-lg text-sm">
              <option value="">{loading ? 'Loading…' : 'Select job…'}</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.job_card_no} — {j.drawing_no || 'no drawing'}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Shift</label>
            <select value={shift} onChange={(e) => setShift(e.target.value)} data-testid="iq-shift"
              className="w-full px-3 py-3 border border-slate-300 rounded-lg text-sm">
              <option>General A</option><option>General B</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">Machine</label>
            <select value={machine} onChange={(e) => setMachine(e.target.value)} data-testid="iq-machine"
              className="w-full px-3 py-3 border border-slate-300 rounded-lg text-sm">
              <option value="">{machines.length ? 'Select machine…' : 'No machines for stage'}</option>
              {machines.map((m) => <option key={m.id} value={`${m.machine_no}`.trim()}>{`${m.machine_no}`.trim()} — {m.machine_name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Operator Name</label>
            <div data-testid="iq-operator">
              <OperatorSelect
                value={operator}
                employeeId={operatorEmpId}
                machineId={machines.find((m) => `${m.machine_no}`.trim() === machine)?.id || ''}
                stageMachineIds={machines.map((m) => m.id)}
                onChange={({ employee_id, operator_name }) => { setOperator(operator_name); setOperatorEmpId(employee_id); }}
              />
            </div>
          </div>
          <div className="sm:col-span-2 flex items-end">
            <button onClick={startInspection} disabled={starting || !jobId} data-testid="iq-start-btn"
              className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardCheck className="w-4 h-4" />} Start Checksheet
            </button>
          </div>
        </div>
        {otherMachineSheet && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2 text-xs text-amber-900" data-testid="iq-parallel-machine-warning">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
            <div className="flex-1">
              <div className="font-semibold">
                Job {otherMachineSheet.job_card_no} already has a live sheet on M/c {mcLabel(otherMachineSheet)} (Shift {otherMachineSheet.shift})
                {(otherMachineSheet.readings?.length || 0) > 0 && ` · ${otherMachineSheet.readings.length} check${otherMachineSheet.readings.length === 1 ? '' : 's'} so far`}
                {otherMachineSheet.operator_name && ` · Operator ${otherMachineSheet.operator_name}`}
              </div>
              <div className="text-amber-800/80 mt-0.5">
                Starting a new sheet on M/c {machine} will run in parallel. Each machine&apos;s sheet is signed independently.
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => openInspection(otherMachineSheet.id)}
                  data-testid="iq-open-other-machine-sheet"
                  className="px-2.5 py-1 rounded-md bg-white border border-amber-300 text-amber-800 text-[11px] font-semibold hover:bg-amber-100">
                  Open sheet on M/c {mcLabel(otherMachineSheet)}
                </button>
              </div>
            </div>
          </div>
        )}
      </CardContent></Card>
      )}

      {/* Today's sheets (merged coverage board) */}
      <CoverageBoard inspections={allInsp} onOpen={openInspection} onRefresh={loadAll}
        action={entryAllowed && !showNewForm ? (
          <button onClick={() => setShowNewForm(true)} data-testid="iq-new-sheet-btn"
            className="px-3.5 py-1.5 rounded-lg text-xs font-bold bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" /> New Sheet
          </button>
        ) : null} />

      {/* Recent — last 7 days with filters */}
      <RecentList inspections={allInsp} templates={templates} onOpen={openInspection}
        search={search} setSearch={setSearch} stageFilter={stageFilter} setStageFilter={setStageFilter}
        statusFilter={statusFilter} setStatusFilter={setStatusFilter} olderCount={olderCount} setOlderCount={setOlderCount} />
      </>)}

      {tab === 'verify' && verifyAllowed && <VerifyQueue inspections={allInsp} onOpen={openInspection} />}
      {tab === 'templates' && templatesAllowed && <TemplatesTab templates={templates} onReloaded={loadTemplates} />}
      {tab === 'archive' && me.role === 'Admin' && <ArchiveList onRestored={loadAll} onOpen={openInspection} />}
    </div>
  );
}

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  return !isNaN(d.getTime()) && d.toDateString() === new Date().toDateString();
};

function RecentList({ inspections, templates, onOpen, search, setSearch, stageFilter, setStageFilter, statusFilter, setStatusFilter, olderCount, setOlderCount }) {
  const matches = (i) => {
    const q = search.toLowerCase();
    if (q && !(i.job_card_no || '').toLowerCase().includes(q)
      && !(i.drawing_number || '').toLowerCase().includes(q)
      && !(i.operator_name || '').toLowerCase().includes(q)) return false;
    if (stageFilter !== 'all' && i.stage_index !== Number(stageFilter)) return false;
    if (statusFilter !== 'all' && i.status !== statusFilter) return false;
    return true;
  };
  const filtered = inspections.filter(matches);
  const shown = filtered.slice(0, 20 + olderCount);
  const hasMore = filtered.length > shown.length;
  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
          <ClipboardList className="w-4 h-4" /> Recent <span className="text-xs font-normal text-slate-400">showing {shown.length} of {filtered.length}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="iq-search"
            placeholder="Search job / drawing / operator…" className="px-3 py-1.5 border border-slate-300 rounded-lg text-xs w-48" />
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="iq-filter-stage"
            className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs bg-white">
            <option value="all">All stages</option>
            {templates.map((t) => <option key={t.stage_index} value={t.stage_index}>{t.stage_name}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="iq-filter-status"
            className="px-2 py-1.5 border border-slate-300 rounded-lg text-xs bg-white">
            <option value="all">All status</option>
            <option value="open">In Progress</option>
            <option value="attention">Needs Attention</option>
            <option value="lab_pending">Verified (Lab Pending)</option>
            <option value="verified">Verified</option>
          </select>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="iq-inspections-list">
        {shown.length === 0 ? (
          <div className="text-sm text-slate-400 py-8 text-center">No matching checksheets in the last 7 days.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5 text-left font-bold">Stage</th>
                  <th className="px-3 py-2.5 text-left font-bold">Job Card</th>
                  <th className="px-3 py-2.5 text-left font-bold">Date</th>
                  <th className="px-3 py-2.5 text-left font-bold">Drawing</th>
                  <th className="px-3 py-2.5 text-left font-bold hidden md:table-cell">Machine</th>
                  <th className="px-3 py-2.5 text-left font-bold hidden md:table-cell">Operator</th>
                  <th className="px-3 py-2.5 text-center font-bold">Checks</th>
                  <th className="px-3 py-2.5 text-center font-bold">NG</th>
                  <th className="px-3 py-2.5 text-left font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((ins) => {
                  const sm = STATUS_META[ins.status] || STATUS_META.open;
                  const failCount = (ins.readings || []).filter((r) => r.any_fail).length;
                  return (
                    <tr key={ins.id} onClick={() => onOpen(ins.id)} data-testid={`iq-open-${ins.id}`}
                      className={`border-b border-slate-50 last:border-0 cursor-pointer transition-colors hover:bg-indigo-50/40 ${ins.status === 'verified' ? 'opacity-60' : ''} ${failCount > 0 && ins.status !== 'verified' ? 'bg-red-50/40' : ''}`}>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{ins.stage_name}</td>
                      <td className="px-3 py-3 font-semibold text-slate-800 whitespace-nowrap">{ins.job_card_no}</td>
                      <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{fmtDate(ins.date)}</td>
                      <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{ins.drawing_number || '—'}</td>
                      <td className="px-3 py-3 text-slate-600 hidden md:table-cell whitespace-nowrap">{mcLabel(ins)}</td>
                      <td className="px-3 py-3 text-slate-600 hidden md:table-cell whitespace-nowrap">{ins.operator_name || '—'}</td>
                      <td className="px-3 py-3 text-center font-mono text-slate-700">{ins.readings?.length || 0}</td>
                      <td className="px-3 py-3 text-center">
                        {failCount > 0 ? (
                          <span className="inline-flex items-center gap-1 text-red-600 font-bold font-mono">
                            <AlertTriangle className="w-3.5 h-3.5" />{failCount}
                          </span>
                        ) : <span className="text-slate-300">0</span>}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <Badge variant="outline" className={`text-[10px] ${sm.cls}`}>{sm.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {hasMore && (
          <div className="p-3 border-t border-slate-100 text-center">
            <button onClick={() => setOlderCount(olderCount + 20)} data-testid="iq-load-older"
              className="px-4 py-2 rounded-lg text-xs font-semibold border border-slate-200 text-slate-600 hover:border-slate-400">
              Load older ({filtered.length - shown.length} more)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const freshness = (ins) => {
  if (ins.status === 'verified') return { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Verified' };
  if (ins.status === 'lab_pending') return { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Lab Pending' };
  const hourly = (ins.readings || []).filter((r) => (r.section || 'hourly') === 'hourly' && r.recorded_at);
  if (!hourly.length) return { cls: 'bg-slate-100 text-slate-500 border-slate-200', label: 'No readings yet' };
  const last = Math.max(...hourly.map((r) => new Date(r.recorded_at).getTime()));
  const mins = Math.max(0, Math.round((Date.now() - last) / 60000));
  const ago = mins < 60 ? `${mins}m ago` : `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
  if (!(ins.frequency || '').toLowerCase().startsWith('hour')) return { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: ago };
  if (mins <= 60) return { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: ago };
  if (mins <= 90) return { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: `${ago} — due` };
  return { cls: 'bg-red-50 text-red-700 border-red-200', label: `${ago} — overdue` };
};

function ArchiveList({ onRestored, onOpen }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/api/inline-quality/inspections-archived`, auth()); setItems(r.data || []); }
    catch { toast.error('Failed to load archive'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const daysLeft = (iso) => {
    const t = new Date(iso).getTime();
    if (isNaN(t)) return '—';
    return Math.max(0, 30 - Math.floor((Date.now() - t) / 86400000));
  };
  const restore = async (id) => {
    try {
      await axios.post(`${API}/api/inline-quality/inspections/${id}/restore`, {}, auth());
      toast.success('Checksheet restored');
      load(); onRestored();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Restore failed'); }
  };
  return (
    <div className="space-y-3" data-testid="iq-archive-list">
      <div className="text-sm font-bold text-slate-700">Archived (deleted) checksheets — auto-purged after 30 days</div>
      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 py-10 text-center text-sm text-slate-400">Archive is empty.</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {items.map((ins) => (
            <div key={ins.id} className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`iq-archive-row-${ins.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800">{ins.stage_name} — {ins.job_card_no}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {fmtDate(ins.date)} · {ins.readings?.length || 0} checks · deleted by {ins.deleted_by_name || '—'} on {fmtDate(ins.deleted_at)} ·
                  <span className="text-amber-600 font-semibold"> {daysLeft(ins.deleted_at)} days left</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => onOpen(ins.id)} data-testid={`iq-archive-view-${ins.id}`}
                  className="px-3.5 py-2 rounded-lg text-xs font-bold border border-slate-200 text-slate-600 hover:border-slate-400 bg-white">
                  View
                </button>
                <button onClick={() => restore(ins.id)} data-testid={`iq-restore-${ins.id}`}
                  className="px-3.5 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5">
                  <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CoverageBoard({ inspections, onOpen, onRefresh, action }) {
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((n) => n + 1), 60000); return () => clearInterval(t); }, []);
  const today = inspections.filter((i) => isToday(i.created_at) || isToday(i.date));

  const groups = useMemo(() => {
    const byStage = new Map();
    today.forEach((ins) => {
      const key = ins.stage_index != null ? ins.stage_index : 999;
      if (!byStage.has(key)) byStage.set(key, { stage_index: key, stage_name: ins.stage_name, sheets: [] });
      byStage.get(key).sheets.push(ins);
    });
    return Array.from(byStage.values())
      .sort((a, b) => a.stage_index - b.stage_index)
      .map((g) => ({
        ...g,
        sheets: g.sheets.slice().sort((a, b) => mcLabel(a).localeCompare(mcLabel(b), undefined, { numeric: true })),
      }));
  }, [today]);

  return (
    <div className="space-y-4" data-testid="iq-coverage-board">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-bold text-slate-700">Today&apos;s sheets — {today.length}</div>
        <div className="flex items-center gap-2">
          <button onClick={onRefresh} data-testid="iq-coverage-refresh"
            className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 text-slate-600 hover:border-slate-400 flex items-center gap-1.5">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          {action}
        </div>
      </div>
      {today.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 py-10 text-center text-sm text-slate-400">No checksheets started today yet.</div>
      ) : (
        groups.map((g) => {
          const groupNg = g.sheets.reduce((sum, ins) => sum + (ins.readings || []).filter((r) => r.any_fail).length, 0);
          const groupOverdue = g.sheets.filter((ins) => freshness(ins).label.includes('overdue')).length;
          return (
            <div key={g.stage_index} data-testid={`iq-coverage-group-${g.stage_index}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">{g.stage_name}</div>
                <span className="text-[11px] text-slate-400 font-mono">{g.sheets.length} sheet{g.sheets.length === 1 ? '' : 's'}</span>
                {groupOverdue > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                    {groupOverdue} overdue
                  </span>
                )}
                {groupNg > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    <AlertTriangle className="w-3 h-3" />{groupNg} NG
                  </span>
                )}
                <div className="flex-1 h-px bg-slate-100" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {g.sheets.map((ins) => {
                  const f = freshness(ins);
                  const ng = (ins.readings || []).filter((r) => r.any_fail).length;
                  return (
                    <button key={ins.id} onClick={() => onOpen(ins.id)} data-testid={`iq-coverage-card-${ins.id}`}
                      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-indigo-300 transition-colors space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-bold text-slate-800 leading-tight">{mcLabel(ins)}</div>
                        <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${f.cls}`}>
                          <Clock className="w-3 h-3" />{f.label}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500">{ins.job_card_no} · {ins.drawing_number || 'no drawing'}</div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                        <span>Shift: <b className="text-slate-700">{ins.shift || '—'}</b></span>
                        <span>Checks: <b className="text-slate-700 font-mono">{ins.readings?.length || 0}</b></span>
                        {ng > 0 && <span className="text-red-600 font-bold flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" />{ng} NG</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function VerifyQueue({ inspections, onOpen }) {
  const queue = inspections.filter((i) => i.status !== 'verified')
    .sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
  return (
    <div className="space-y-3" data-testid="iq-verify-queue">
      <div className="text-sm font-bold text-slate-700">Awaiting sign-off — {queue.length} sheet{queue.length === 1 ? '' : 's'}</div>
      {queue.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 py-10 text-center text-sm text-slate-400 flex flex-col items-center gap-2">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" /> All sheets verified. Nothing pending.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {queue.map((ins) => {
            const ng = (ins.readings || []).filter((r) => r.any_fail).length;
            const live = isToday(ins.created_at) || isToday(ins.date);
            return (
              <button key={ins.id} onClick={() => onOpen(ins.id)} data-testid={`iq-verify-row-${ins.id}`}
                className="w-full text-left px-4 py-3 hover:bg-indigo-50/40 transition-colors flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 flex items-center gap-2 flex-wrap">
                    {ins.stage_name} — {ins.job_card_no}
                    {live && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Live today</Badge>}
                    {ins.status === 'attention' && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700 border-red-200">Needs Attention</Badge>}
                    {ins.status === 'lab_pending' && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Lab Pending</Badge>}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {fmtDate(ins.date)} · Shift {ins.shift || '—'} · M/c {mcLabel(ins)} · {ins.readings?.length || 0} checks
                    {ng > 0 && <span className="text-red-600 font-bold"> · {ng} NG</span>}
                  </div>
                </div>
                <ChevronLeft className="w-4 h-4 text-slate-300 rotate-180 shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const SECTION_META = {
  hourly: { title: 'In-Process Checks — इन-प्रोसेस', labelKind: 'time', hint: '' },
  daily_lab: { title: 'Lab — Daily Destructive Sample — लैब (प्रतिदिन)', labelKind: 'date', hint: '1 sample / day · from lab report' },
  per_heat: { title: 'Lab — Per Heat — प्रति हीट (LHB parts only)', labelKind: 'text', hint: '1 spring / heat · from lab report' },
};

const canVerify = (u) => !!u && (u.role === 'Admin' ||
  u.module_permissions?.inlineQuality?.verify === true ||
  u.permissions?.inlineQuality?.verify === true);

const canEnter = (u) => hasPermission(u, 'inlineQuality', 'enter');

// Template editor gate: same as canVerify (Admin OR inlineQuality.verify permission)
const canEditTemplates = (u) => canVerify(u);

function ChecksheetView({ inspection, onBack, onRefresh }) {
  const [saving, setSaving] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const verifyAllowed = canVerify(user);
  const entryAllowed = canEnter(user);
  const sm = STATUS_META[inspection.status] || STATUS_META.open;
  const isArchived = !!inspection.deleted;
  const allSpecs = [...(inspection.specs || [])].sort((a, b) => a.display_order - b.display_order);
  const allReadings = inspection.readings || [];
  const secOf = (x) => x.section || 'hourly';
  const sections = ['hourly', 'daily_lab', 'per_heat'].filter((id) => allSpecs.some((s) => secOf(s) === id));

  const verify = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/inline-quality/inspections/${inspection.id}/verify`, {}, auth());
      toast.success(r.data?.status === 'lab_pending' ? 'Signed — awaiting per-heat lab results' : 'Verified & signed');
      await onRefresh();
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const labVerify = async () => {
    setSaving(true);
    try { await axios.put(`${API}/api/inline-quality/inspections/${inspection.id}/lab-verify`, {}, auth()); toast.success('Lab results signed — sheet fully verified'); await onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4" data-testid="iq-checksheet">
      <div className="flex items-center justify-between gap-2">
        <button onClick={onBack} data-testid="iq-back-btn" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          {verifyAllowed && !isArchived && (
            <button onClick={async () => {
              if (!window.confirm(`Delete this checksheet (${inspection.stage_name} — ${inspection.job_card_no})? It will be archived for 30 days; only an Admin can restore it.`)) return;
              try {
                await axios.delete(`${API}/api/inline-quality/inspections/${inspection.id}`, auth());
                toast.success('Checksheet deleted (archived 30 days)');
                onBack();
              } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
            }} data-testid="iq-delete-btn"
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold border border-red-200 text-red-600 hover:bg-red-50 bg-white">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
          <button onClick={() => printChecksheet(inspection)} data-testid="iq-print-btn"
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold border border-slate-200 text-slate-600 hover:border-slate-400 bg-white">
            <Printer className="w-4 h-4" /> Print / PDF
          </button>
        </div>
      </div>

      {isArchived && (
        <div className="flex items-center justify-between gap-3 flex-wrap bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="iq-archived-banner">
          <div className="text-sm text-amber-800 font-semibold flex items-center gap-2">
            <Trash2 className="w-4 h-4" /> Archived checksheet (read-only) — deleted by {inspection.deleted_by_name || '—'} on {fmtDate(inspection.deleted_at)}
          </div>
          {user.role === 'Admin' && (
            <button onClick={async () => {
              try {
                await axios.post(`${API}/api/inline-quality/inspections/${inspection.id}/restore`, {}, auth());
                toast.success('Checksheet restored');
                await onRefresh();
              } catch (e) { toast.error(e?.response?.data?.detail || 'Restore failed'); }
            }} data-testid="iq-restore-inline-btn"
              className="px-3.5 py-2 rounded-lg text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5">
              <ArchiveRestore className="w-3.5 h-3.5" /> Restore
            </button>
          )}
        </div>
      )}


      <Card><CardContent className="p-0">
        <div className="bg-indigo-700 rounded-t-xl px-4 sm:px-5 py-3.5 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-base sm:text-lg font-bold text-white leading-tight" data-testid="iq-sheet-title">
              {inspection.title_en || `Daily Validation Checksheet — ${inspection.stage_name}`}
            </div>
            <div className="text-[11px] text-indigo-200 mt-0.5" data-testid="iq-sheet-subtitle">
              {inspection.title_hi || ''}{inspection.doc_no ? ` · ${inspection.doc_no}` : ''}
            </div>
          </div>
          <Badge variant="outline" className={`text-[11px] bg-white ${sm.cls}`}>{sm.label}</Badge>
        </div>
        <div className="px-4 sm:px-5 py-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-x-4 gap-y-2.5" data-testid="iq-sheet-info">
          {[
            ['Job Card No.', inspection.job_card_no, 'iq-info-job'],
            ['Drawing No.', inspection.drawing_number || '—', 'iq-info-drawing'],
            ['Heat / Lot No.', (inspection.heat_numbers && inspection.heat_numbers.length) ? inspection.heat_numbers.join(', ') : '—', 'iq-info-heat'],
            ['Date', fmtDate(inspection.date), 'iq-info-date'],
            ['Shift', inspection.shift || '—', 'iq-info-shift'],
            ['Machine', inspection.machine_name ? `${inspection.machine_no} — ${inspection.machine_name}` : (inspection.machine_no || '—'), 'iq-info-machine'],
            ['Operator', inspection.operator_name || '—', 'iq-info-operator'],
            ['Sample Size', sampleLabel(inspection.sample_size, inspection.frequency || 'Hourly'), 'iq-info-sample'],
          ].map(([label, value, tid]) => (
            <div key={tid} data-testid={tid} className="min-w-0">
              <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
              <div className="text-sm font-semibold text-slate-800 truncate" title={String(value)}>{value}</div>
            </div>
          ))}
        </div>
      </CardContent></Card>

      {sections.map((id) => (
        <SectionGrid key={id} inspection={inspection} section={id}
          editable={entryAllowed && !isArchived && (inspection.status === 'lab_pending' ? (id === 'per_heat' || id === 'daily_lab') : inspection.status !== 'verified')}
          canEdit={verifyAllowed && !isArchived && (inspection.status === 'lab_pending' ? (id === 'per_heat' || id === 'daily_lab') : inspection.status !== 'verified')}
          specs={allSpecs.filter((s) => secOf(s) === id)}
          readings={allReadings.filter((r) => secOf(r) === id)}
          onRefresh={onRefresh} />
      ))}

      {inspection.status !== 'verified' && inspection.status !== 'lab_pending' && !isArchived && verifyAllowed && (
        <button onClick={verify} disabled={saving} data-testid="iq-verify-btn"
          className="w-full sm:w-auto px-5 py-3 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> Verify &amp; Sign
        </button>
      )}
      {inspection.status === 'lab_pending' && !isArchived && (
        <div className="space-y-2" data-testid="iq-lab-pending-block">
          <div className="text-sm text-amber-800 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3.5 py-2.5">
            <Clock className="w-4 h-4 shrink-0" /> Production signed by <b>{inspection.verified_by_name}</b> on {fmtDate(inspection.verified_at)} — awaiting per-heat lab results. Lab entries can still be added above.
          </div>
          {verifyAllowed && (
            <button onClick={labVerify} disabled={saving} data-testid="iq-lab-verify-btn"
              className="w-full sm:w-auto px-5 py-3 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Lab Sign-off — Close Sheet
            </button>
          )}
        </div>
      )}
      {inspection.status !== 'verified' && inspection.status !== 'lab_pending' && !isArchived && !verifyAllowed && (
        <div className="text-xs text-slate-400" data-testid="iq-verify-locked">Verification is done by an authorised user (Admin / granted in User Management).</div>
      )}
      {inspection.status === 'verified' && (
        <div className="text-sm text-emerald-700 flex items-center gap-2" data-testid="iq-verified-note">
          <CheckCircle2 className="w-4 h-4" /> Verified by {inspection.verified_by_name}
          {inspection.lab_verified_by_name && <span className="text-slate-500">· Lab sign-off: <b className="text-emerald-700">{inspection.lab_verified_by_name}</b> ({fmtDate(inspection.lab_verified_at)})</span>}
        </div>
      )}
    </div>
  );
}

function SectionGrid({ inspection, section, specs, readings, onRefresh, editable, canEdit }) {
  const meta = SECTION_META[section] || SECTION_META.hourly;
  const isHourly = section === 'hourly';
  const tid = (base) => (isHourly ? base : `${base}-${section}`);
  const [form, setForm] = useState({});
  const [remarks, setRemarks] = useState('');
  const [editing, setEditing] = useState(null);
  const [correctionReason, setCorrectionReason] = useState('');
  const [locations, setLocations] = useState({});  // { [spec.key]: [{label,value}, ...] }
  const [label, setLabel] = useState(
    meta.labelKind === 'time' ? new Date().toTimeString().slice(0, 5)
      : meta.labelKind === 'date' ? new Date().toISOString().slice(0, 10)
        : ''
  );
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const scrollRef = useRef(null);
  const hiddenCount = showAll ? 0 : Math.max(0, readings.length - 8);
  const shownReadings = showAll ? readings : readings.slice(-8);
  const newColLabel = editing ? 'Correction' : (meta.labelKind === 'date' ? fmtDate(label) : (label || 'New'));
  const showEntry = editable || !!editing;

  const startEdit = (r) => {
    setEditing(r);
    const f = {};
    const locs = {};
    Object.entries(r.values || {}).forEach(([k, cell]) => {
      const sp = specs.find((s) => s.key === k);
      if (sp?.computed) return;
      if (sp?.multi_location && Array.isArray(cell?.locations) && cell.locations.length) {
        locs[k] = cell.locations.map((l) => ({ label: l.label || '', value: l.value || '' }));
        f[k] = '__multi__';   // sentinel — actual data lives in locations[k]
      } else {
        f[k] = cell?.value ?? '';
      }
    });
    setForm(f);
    setLocations(locs);
    setRemarks(r.remarks || '');
    if (meta.labelKind === 'time') setLabel(r.time || '');
    else if (meta.labelKind === 'text') setLabel(r.label || '');
    else {
      const d = new Date(r.label);
      setLabel(isNaN(d.getTime()) ? new Date().toISOString().slice(0, 10) : d.toISOString().slice(0, 10));
    }
  };

  const cancelEdit = () => {
    setEditing(null); setForm({}); setRemarks(''); setCorrectionReason(''); setLocations({});
    setLabel(meta.labelKind === 'time' ? new Date().toTimeString().slice(0, 5)
      : meta.labelKind === 'date' ? new Date().toISOString().slice(0, 10) : '');
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [readings.length, showAll]);

  // Auto-init a single L1 row for multi-location specs whenever the entry form is open (new entry, not editing)
  useEffect(() => {
    if (!showEntry || editing) return;
    const missing = specs.filter((s) => s.multi_location && !locations[s.key]);
    if (!missing.length) return;
    setLocations((cur) => {
      const next = { ...cur };
      missing.forEach((s) => { next[s.key] = [{ label: 'L1', value: '' }]; });
      return next;
    });
    setForm((f) => {
      const next = { ...f };
      missing.forEach((s) => { next[s.key] = '__multi__'; });
      return next;
    });
  }, [showEntry, editing, specs]);

  const setVal = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // Multi-location helpers
  const getLocs = (key) => locations[key] || [];
  const ensureLocs = (key) => {
    if (!locations[key]) {
      const initial = [{ label: 'L1', value: '' }, { label: 'L2', value: '' }];
      setLocations((s) => ({ ...s, [key]: initial }));
      setForm((f) => ({ ...f, [key]: '__multi__' }));
      return initial;
    }
    return locations[key];
  };
  const setLocVal = (key, idx, field, value) => {
    setLocations((s) => {
      const arr = [...(s[key] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...s, [key]: arr };
    });
    setForm((f) => ({ ...f, [key]: '__multi__' }));
  };
  const addLoc = (key) => {
    setLocations((s) => {
      const arr = [...(s[key] || [])];
      arr.push({ label: `L${arr.length + 1}`, value: '' });
      return { ...s, [key]: arr };
    });
    setForm((f) => ({ ...f, [key]: '__multi__' }));
  };
  const removeLoc = (key, idx) => {
    setLocations((s) => {
      const arr = [...(s[key] || [])];
      arr.splice(idx, 1);
      return { ...s, [key]: arr.length ? arr : undefined };
    });
    if ((locations[key] || []).length <= 1) setForm((f) => { const { [key]: _, ...rest } = f; return rest; });
  };

  const submitReading = async () => {
    const values = {};
    Object.keys(form).forEach((k) => {
      const sp = specs.find((s) => s.key === k);
      if (sp?.multi_location) {
        const locs = (locations[k] || []).filter((l) => String(l.value).trim() !== '');
        if (locs.length) values[k] = { value: '', locations: locs };
        return;
      }
      if (form[k] !== '' && form[k] != null && form[k] !== '__multi__') values[k] = { value: String(form[k]) };
    });
    if (Object.keys(values).length === 0) { toast.error('Enter at least one value'); return; }
    if (!isHourly && !label) { toast.error(meta.labelKind === 'text' ? 'Enter heat no.' : 'Pick sample date'); return; }
    if (editing && correctionReason.trim().length < 3) { toast.error('Please enter a reason for the correction (min 3 chars)'); return; }
    setSaving(true);
    const payload = { time: isHourly ? label : '', label: isHourly ? '' : (meta.labelKind === 'date' ? fmtDate(label) : label), section, values, remarks };
    if (editing) payload.correction_reason = correctionReason.trim();
    try {
      if (editing) {
        await axios.put(`${API}/api/inline-quality/inspections/${inspection.id}/readings/${editing.id}`, payload, auth());
        toast.success('Correction saved');
      } else {
        await axios.post(`${API}/api/inline-quality/inspections/${inspection.id}/readings`, payload, auth());
        toast.success('Saved');
      }
      cancelEdit();
      await onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const liveEval = (spec, v) => {
    if (v === '' || v == null) return null;
    if (spec.type === 'attribute') return null;
    if (spec.type === 'text') return null;
    const n = parseFloat(v);
    if (isNaN(n) || (spec.low == null && spec.high == null)) return null;
    if (spec.low != null && n < spec.low) return false;
    if (spec.high != null && n > spec.high) return false;
    return true;
  };

  const computeVal = (s) => {
    const nums = (s.compute_sources || []).map((k) => {
      const src = specs.find((x) => x.key === k);
      const raw = src?.computed ? computeVal(src) : form[k];
      return parseFloat(raw);
    });
    if (!nums.length || nums.some((n) => isNaN(n))) return '';
    if (s.computed === 'avg') return (nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2);
    if (s.computed === 'diff_abs' && nums.length === 2) return Math.abs(nums[0] - nums[1]).toFixed(2);
    return '';
  };

  return (
      <Card><CardContent className="p-0">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm font-bold text-slate-700 flex items-center gap-2 flex-wrap">
            {meta.title} <span className="text-xs font-normal text-slate-400">({readings.length} {isHourly ? 'readings' : 'entries'}){meta.hint ? ` · ${meta.hint}` : ''}</span>
            {section === 'per_heat' && (inspection.heat_numbers || []).length > 0 && (() => {
              const heats = inspection.heat_numbers || [];
              const tested = heats.filter((h) => readings.some((r) => r.label === h));
              return (
                <span data-testid="iq-heat-coverage"
                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${tested.length === heats.length ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                  {tested.length}/{heats.length} heat{heats.length === 1 ? '' : 's'} tested{tested.length < heats.length ? ` · pending: ${heats.filter((h) => !tested.includes(h)).join(', ')}` : ''}
                </span>
              );
            })()}
            {readings.length > 8 && (
              <button onClick={() => setShowAll((v) => !v)} data-testid={tid('iq-toggle-columns')}
                className="px-2.5 py-1 rounded-full text-[11px] font-semibold border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100">
                {showAll ? 'Show last 8' : `Show all ${readings.length}`}
              </button>
            )}
          </div>
          {showEntry && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{editing ? 'Correcting' : meta.labelKind === 'time' ? 'New reading @' : meta.labelKind === 'date' ? 'Sample date' : 'Heat No.'}</span>
              {meta.labelKind === 'time' && (
                <input type="time" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="iq-reading-time"
                  className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm" />
              )}
              {meta.labelKind === 'date' && (
                <input type="date" value={label} onChange={(e) => setLabel(e.target.value)} data-testid={tid('iq-reading-date')}
                  className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm" />
              )}
              {meta.labelKind === 'text' && (
                (inspection.heat_numbers || []).length ? (
                  <select value={label} onChange={(e) => setLabel(e.target.value)} data-testid={tid('iq-reading-heat')}
                    className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white">
                    <option value="">Select heat…</option>
                    {(inspection.heat_numbers || []).map((h) => <option key={h} value={h}>{h}</option>)}
                  </select>
                ) : (
                  <span className="text-xs text-red-500" data-testid={tid('iq-no-heats')}>No heat on job card</span>
                )
              )}
            </div>
          )}
        </div>
        {showEntry && (
          <div className={`md:hidden border-b border-slate-100 p-3 space-y-2 ${editing ? 'bg-amber-50/40' : 'bg-indigo-50/30'}`} data-testid={tid('iq-mobile-entry')}>
            <div className={`text-[11px] font-bold uppercase tracking-wide ${editing ? 'text-amber-700' : 'text-indigo-700'}`}>
              {editing ? `Correcting — ${editing.time || editing.label || 'entry'}` : `New entry — ${newColLabel}`}
            </div>
            {specs.map((s) => {
              const v = s.computed ? computeVal(s) : (form[s.key] ?? '');
              const ev = liveEval(s, v);
              if (s.multi_location) {
                const locs = getLocs(s.key);
                const rows = locs.length ? locs : [{ label: 'L1', value: '' }];
                return (
                  <div key={s.key} className="bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={`iq-mobile-row-${s.key}`}>
                    <div className="mb-1.5">
                      <div className="text-xs font-semibold text-slate-700 leading-tight">{s.name_en}</div>
                      <div className="text-[10px] text-slate-400">{s.spec_display}{s.unit ? ` ${s.unit}` : ''} · multi-location</div>
                    </div>
                    {rows.map((L, idx) => {
                      const lev = liveEval(s, L.value);
                      const isLast = idx === rows.length - 1;
                      return (
                        <div key={idx} className="flex items-center gap-1.5 mb-1.5" data-testid={`iq-mob-${s.key}-loc-${idx}`}>
                          <div className="w-10 flex-none px-1 py-2 rounded bg-slate-100 border border-slate-200 text-center text-[11px] font-bold text-slate-500 font-mono">{L.label || `L${idx + 1}`}</div>
                          <input value={L.value} onChange={(e) => setLocVal(s.key, idx, 'value', e.target.value)}
                            inputMode="decimal" data-testid={`iq-mob-${s.key}-loc-${idx}-value`}
                            className={`flex-1 min-w-0 px-3 py-2 border rounded-lg text-sm text-center font-mono ${lev === false ? 'border-red-400 bg-red-50 text-red-700 font-bold' : lev === true ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300'}`}
                            placeholder={s.unit || '—'} />
                          {rows.length > 1 && (
                            <button onClick={() => removeLoc(s.key, idx)} data-testid={`iq-mob-${s.key}-loc-${idx}-remove`}
                              className="w-8 h-8 flex-none text-red-400 hover:bg-red-50 rounded text-lg leading-none" title="Remove">×</button>
                          )}
                          {isLast && (
                            <button onClick={() => (locs.length ? addLoc(s.key) : ensureLocs(s.key))} data-testid={`iq-mob-${s.key}-add-loc`}
                              className="w-8 h-8 flex-none rounded-full bg-indigo-500 hover:bg-indigo-600 text-white font-bold text-base leading-none" title="Add location">+</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              }
              return (
                <div key={s.key} className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between gap-2" data-testid={`iq-mobile-row-${s.key}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-slate-700 leading-tight">{s.name_en}</div>
                    <div className="text-[10px] text-slate-400">{s.spec_display || (s.type === 'attribute' ? 'Ok / Not-Ok' : s.type === 'text' ? 'note' : '')}{s.unit ? ` ${s.unit}` : ''}</div>
                  </div>
                  {s.type === 'attribute' ? (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => setVal(s.key, v === 'Ok' ? '' : 'Ok')} data-testid={`iq-mob-${s.key}-ok`}
                        className={`px-3 py-2 rounded text-[11px] font-bold border ${v === 'Ok' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-200'}`}>OK</button>
                      <button onClick={() => setVal(s.key, v === 'Not Ok' ? '' : 'Not Ok')} data-testid={`iq-mob-${s.key}-notok`}
                        className={`px-3 py-2 rounded text-[11px] font-bold border ${v === 'Not Ok' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-200'}`}>NG</button>
                    </div>
                  ) : s.computed ? (
                    <div className="flex items-center gap-1 shrink-0" data-testid={`iq-mob-auto-${s.key}`}>
                      <span className={`px-2 py-1.5 rounded text-sm font-mono font-bold ${ev === false ? 'bg-red-100 text-red-700' : ev === true ? 'bg-emerald-100 text-emerald-700' : 'text-indigo-700'}`}>
                        {v || '—'}
                      </span>
                      <span className="text-[9px] text-slate-400 uppercase">auto</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 shrink-0">
                      <input value={v} onChange={(e) => setVal(s.key, e.target.value)} data-testid={`iq-mob-${s.key}`}
                        inputMode={s.type === 'numeric' ? 'decimal' : 'text'}
                        className={`w-24 px-2 py-2 border rounded text-sm text-center font-mono ${ev === false ? 'border-red-400 bg-red-50 text-red-700' : ev === true ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300'}`}
                        placeholder={s.unit || '—'} />
                      {ev === false && <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                      {ev === true && <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />}
                    </div>
                  )}
                </div>
              );
            })}
            <input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid={tid('iq-mob-remarks')}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" placeholder="Remarks… / रिमार्क्स" />
            {editing && (
              <input value={correctionReason} onChange={(e) => setCorrectionReason(e.target.value)} data-testid={tid('iq-mob-correction-reason')}
                className="w-full px-3 py-2 border border-amber-300 bg-amber-50 rounded-lg text-sm text-amber-900 placeholder-amber-500"
                placeholder="Why is this being corrected? (required)" />
            )}
            <div className="flex gap-2">
              <button onClick={submitReading} disabled={saving} data-testid={tid('iq-mob-save-reading')}
                className={`flex-1 px-5 py-3 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2 ${editing ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {editing ? 'Save Correction' : isHourly ? `Save Reading @ ${label}` : `Save Entry — ${newColLabel}`}
              </button>
              {editing && (
                <button onClick={cancelEdit} data-testid={tid('iq-mob-cancel-edit')}
                  className="px-4 py-3 rounded-lg text-sm font-semibold border border-slate-300 text-slate-600 bg-white">Cancel</button>
              )}
            </div>
          </div>
        )}
        <div className="overflow-x-auto" ref={scrollRef}>
          <table className="text-[11px] border-collapse w-full">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-2 py-2 text-left font-bold text-slate-500 sticky left-0 bg-slate-50 z-10 min-w-[120px] max-w-[120px] md:min-w-[180px] md:max-w-[180px] border-r border-slate-200 md:border-r-0">Characteristic<br /><span className="font-normal text-slate-400">कैरेक्टरिस्टिक</span></th>
              <th className="hidden md:table-cell px-2 py-2 text-left font-bold text-slate-500 md:sticky md:left-[180px] bg-slate-50 z-10 min-w-[110px] max-w-[110px]">Spec</th>
              <th className="hidden md:table-cell px-1 py-2 text-left font-bold text-slate-500 md:sticky md:left-[290px] bg-slate-50 z-10 min-w-[72px] max-w-[72px] border-r border-slate-200">Trend</th>
              {hiddenCount > 0 && <th className="px-2 py-2 font-bold text-slate-400 text-center min-w-[40px]" data-testid="iq-hidden-cols">+{hiddenCount}…</th>}
              {shownReadings.map((r, i) => (
                <th key={r.id} className={`px-2 py-2 font-bold text-center min-w-[60px] ${editing?.id === r.id ? 'bg-amber-50' : ''} ${r.any_fail ? 'text-red-600' : 'text-slate-500'}`} data-testid={`iq-reading-col-${hiddenCount + i}`}>
                  {r.time || r.label || `#${hiddenCount + i + 1}`}{r.any_fail && <AlertTriangle className="w-3 h-3 inline ml-0.5" />}
                  {r.shared && <div className="mt-0.5"><span className="px-1 py-0.5 rounded bg-blue-50 text-blue-600 text-[9px] font-semibold border border-blue-200" title={`Auto-shared from job ${r.source_job || ''}`}>auto · {r.source_job}</span></div>}
                  {canEdit && !r.shared && (
                    <button onClick={() => startEdit(r)} data-testid={`iq-edit-reading-${r.id}`} title="Correct this entry (verified users)"
                      className={`block mx-auto mt-0.5 p-1 rounded transition-colors ${editing?.id === r.id ? 'bg-amber-200 text-amber-800' : 'text-slate-300 hover:text-amber-600 hover:bg-amber-50'}`}>
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </th>
              ))}
              {showEntry && (
                <th className={`hidden md:table-cell px-2 py-2 font-bold text-center min-w-[130px] ${editing ? 'text-amber-700 bg-amber-50' : 'text-indigo-700 bg-indigo-50'}`} data-testid={tid('iq-new-col')}>
                  {editing ? `Correcting ${editing.time || editing.label || ''}` : newColLabel}
                </th>
              )}
            </tr></thead>
            <tbody>
              {specs.map((s) => {
                const v = s.computed ? computeVal(s) : (form[s.key] ?? '');
                const ev = liveEval(s, v);
                return (
                  <tr key={s.key} className="border-b border-slate-50">
                    <td className="px-2 py-1.5 font-medium text-slate-700 sticky left-0 bg-white z-10 max-w-[120px] md:max-w-[180px] border-r border-slate-200 md:border-r-0">
                      {s.name_en}<div className="text-[10px] text-slate-400">{s.name_hi}</div>
                    </td>
                    <td className="hidden md:table-cell px-2 py-1.5 text-[10px] text-slate-500 md:sticky md:left-[180px] bg-white z-10 max-w-[110px]">{s.spec_display || (s.type === 'attribute' ? 'Ok / Not-Ok' : s.type === 'text' ? 'note' : '—')}</td>
                    <td className="hidden md:table-cell px-1 py-1.5 md:sticky md:left-[290px] bg-white z-10 max-w-[72px] border-r border-slate-200" data-testid={`iq-trend-${s.key}`}>
                      {s.type === 'numeric' ? (
                        <Sparkline
                          values={readings.map((r) => parseFloat(r.values?.[s.key]?.value)).filter((n) => !isNaN(n))}
                          low={s.low} high={s.high} />
                      ) : <span className="text-slate-300 text-[10px]">—</span>}
                    </td>
                    {hiddenCount > 0 && <td className="px-2 py-1.5 text-center text-slate-300">…</td>}
                    {shownReadings.map((r) => {
                      const cell = r.values?.[s.key];
                      const pf = cell?.passed;
                      const oldVal = r.edited ? r.original_values?.[s.key]?.value : null;
                      const changed = oldVal != null && oldVal !== '' && String(oldVal) !== String(cell?.value ?? '');
                      const locList = Array.isArray(cell?.locations) ? cell.locations : null;
                      const locTip = locList ? locList.map((L) => `${L.label || ''}: ${L.value}${L.passed === false ? ' ✗' : L.passed === true ? ' ✓' : ''}`).join('\n') : '';
                      const editTip = r.edited ? `Edited by ${r.corrected_by_name || '—'}${r.corrected_at ? ` on ${new Date(r.corrected_at).toLocaleString()}` : ''}${r.correction_reason ? ` · Reason: ${r.correction_reason}` : ''}${changed ? ` · Was: ${oldVal}` : ''}` : '';
                      const tip = [locTip, editTip].filter(Boolean).join('\n');
                      return (
                        <td key={r.id} title={tip}
                          className={`px-2 py-1.5 text-center font-mono ${pf === false ? 'bg-red-50 text-red-700 font-bold' : pf === true ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600'}`}
                          data-testid={changed ? `iq-cell-edited-${r.id}-${s.key}` : undefined}>
                          {locList && locList.length ? (
                            <div className="flex flex-col leading-tight" data-testid={`iq-cell-locs-${r.id}-${s.key}`}>
                              {locList.map((L, i) => (
                                <span key={i} className={`text-[11px] ${L.passed === false ? 'text-red-700 font-bold' : L.passed === true ? 'text-emerald-700' : ''}`}>
                                  {L.value}{i < locList.length - 1 ? ';' : ''}
                                </span>
                              ))}
                            </div>
                          ) : (cell?.value ?? '—')}
                          {changed && <div className="text-[9px] text-amber-700 line-through opacity-70" data-testid={`iq-cell-was-${r.id}-${s.key}`}>was {oldVal}</div>}
                        </td>
                      );
                    })}
                    {showEntry && (
                      <td className={`hidden md:table-cell px-2 py-1.5 text-center ${editing ? 'bg-amber-50/50' : 'bg-indigo-50/40'}`}>
                        {s.multi_location ? (() => {
                          const locs = getLocs(s.key);
                          const rows = locs.length ? locs : [{ label: 'L1', value: '' }];
                          return (
                            <div className="flex flex-col gap-1.5" data-testid={`iq-in-${s.key}-multi`}>
                              {rows.map((L, idx) => {
                                const lev = liveEval(s, L.value);
                                const isLast = idx === rows.length - 1;
                                return (
                                  <div key={idx} className="flex items-center gap-1">
                                    <div className="w-8 flex-none px-1 py-1.5 rounded bg-slate-100 border border-slate-200 text-center text-[10px] font-bold text-slate-500 font-mono">{L.label || `L${idx + 1}`}</div>
                                    <input value={L.value} onChange={(e) => setLocVal(s.key, idx, 'value', e.target.value)}
                                      inputMode="decimal" data-testid={`iq-in-${s.key}-loc-${idx}-value`}
                                      className={`flex-1 min-w-0 px-2 py-1.5 border rounded-lg text-sm text-center font-mono ${lev === false ? 'border-red-400 bg-red-50 text-red-700 font-bold' : lev === true ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300'}`}
                                      placeholder={s.unit || '—'} />
                                    {rows.length > 1 && (
                                      <button onClick={() => removeLoc(s.key, idx)} data-testid={`iq-in-${s.key}-loc-${idx}-remove`}
                                        className="w-6 h-6 flex-none text-red-400 hover:bg-red-50 rounded text-base leading-none" title="Remove">×</button>
                                    )}
                                    {isLast && (
                                      <button onClick={() => (locs.length ? addLoc(s.key) : ensureLocs(s.key))} data-testid={`iq-in-${s.key}-add-loc`}
                                        className="w-6 h-6 flex-none rounded-full bg-indigo-500 hover:bg-indigo-600 text-white font-bold text-sm leading-none" title="Add location">+</button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })() : s.type === 'attribute' ? (
                          <div className="flex gap-1 justify-center">
                            <button onClick={() => setVal(s.key, v === 'Ok' ? '' : 'Ok')} data-testid={`iq-in-${s.key}-ok`}
                              className={`px-2 py-1 rounded text-[10px] font-bold border ${v === 'Ok' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-emerald-700 border-emerald-200'}`}>OK</button>
                            <button onClick={() => setVal(s.key, v === 'Not Ok' ? '' : 'Not Ok')} data-testid={`iq-in-${s.key}-notok`}
                              className={`px-2 py-1 rounded text-[10px] font-bold border ${v === 'Not Ok' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-200'}`}>NG</button>
                          </div>
                        ) : s.computed ? (
                          <div className="flex items-center justify-center gap-1" data-testid={`iq-auto-${s.key}`}>
                            <span className={`px-2 py-1 rounded text-sm font-mono font-bold ${ev === false ? 'bg-red-100 text-red-700' : ev === true ? 'bg-emerald-100 text-emerald-700' : 'text-indigo-700'}`}>
                              {v || '—'}
                            </span>
                            <span className="text-[9px] text-slate-400 uppercase">auto</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1">
                            <input value={v} onChange={(e) => setVal(s.key, e.target.value)} data-testid={`iq-in-${s.key}`}
                              inputMode={s.type === 'numeric' ? 'decimal' : 'text'}
                              className={`w-20 px-2 py-1.5 border rounded text-sm text-center font-mono ${ev === false ? 'border-red-400 bg-red-50 text-red-700' : ev === true ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300'}`}
                              placeholder={s.unit || '—'} />
                            {ev === false && <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                            {ev === true && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <td className="px-2 py-1.5 font-medium text-slate-600 sticky left-0 bg-slate-50 z-10 border-r border-slate-200 md:border-r-0">
                  Remarks<div className="text-[10px] text-slate-400">रिमार्क्स</div>
                </td>
                <td className="hidden md:table-cell px-2 py-1.5 text-[10px] text-slate-400 md:sticky md:left-[180px] bg-slate-50 z-10">—</td>
                <td className="hidden md:table-cell md:sticky md:left-[290px] bg-slate-50 z-10 border-r border-slate-200" />
                {hiddenCount > 0 && <td className="px-2 py-1.5 text-center text-slate-300">…</td>}
                {shownReadings.map((r) => (
                  <td key={r.id} className="px-2 py-1.5 text-center text-[10px] text-slate-600" data-testid={`iq-remarks-${r.id}`}>{r.remarks || '—'}</td>
                ))}
                {showEntry && (
                  <td className={`hidden md:table-cell px-2 py-1.5 text-center ${editing ? 'bg-amber-50/50' : 'bg-indigo-50/40'}`}>
                    <input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid={tid('iq-in-remarks')}
                      className="w-24 px-2 py-1.5 border border-slate-300 rounded text-[11px]" placeholder="Remarks…" />
                  </td>
                )}
              </tr>
              <tr>
                <td className="px-2 py-1.5 text-[10px] text-slate-400 sticky left-0 bg-white z-10 border-r border-slate-200 md:border-r-0">Recorded By / हस्ताक्षर</td>
                <td className="hidden md:table-cell md:sticky md:left-[180px] bg-white z-10" />
                <td className="hidden md:table-cell md:sticky md:left-[290px] bg-white z-10 border-r border-slate-200" />
                {hiddenCount > 0 && <td />}
                {shownReadings.map((r) => (
                  <td key={r.id} className="px-2 py-1.5 text-center text-[10px] text-slate-400">
                    {r.recorded_by_name}
                    {r.corrected_by_name && (
                      <div className="text-amber-600 font-semibold cursor-help" data-testid={`iq-corrected-${r.id}`}
                        title={`Corrected by ${r.corrected_by_name}${r.corrected_at ? ` · ${new Date(r.corrected_at).toLocaleString()}` : ''}${r.correction_reason ? ` · Reason: ${r.correction_reason}` : ''}`}>
                        ✎ {r.corrected_by_name}
                        {r.correction_reason && <div className="text-[9px] text-amber-500 font-normal italic truncate max-w-[110px] mx-auto">{r.correction_reason}</div>}
                      </div>
                    )}
                  </td>
                ))}
                {showEntry && <td className={`hidden md:table-cell ${editing ? 'bg-amber-50/50' : 'bg-indigo-50/40'}`} />}
              </tr>
            </tbody>
          </table>
        </div>
        {showEntry && (
          <div className="p-4 border-t border-slate-100 hidden md:flex items-center gap-2 flex-wrap">
            {editing && (
              <input value={correctionReason} onChange={(e) => setCorrectionReason(e.target.value)} data-testid={tid('iq-correction-reason')}
                className="flex-1 min-w-[240px] px-3 py-3 border border-amber-300 bg-amber-50 rounded-lg text-sm text-amber-900 placeholder-amber-500"
                placeholder="Why is this being corrected? (required — will appear on audit trail & PDF)" />
            )}
            <button onClick={submitReading} disabled={saving} data-testid={tid('iq-save-reading')}
              className={`w-full sm:w-auto px-5 py-3 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2 ${editing ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {editing ? 'Save Correction' : isHourly ? `Save Reading @ ${label}` : `Save Entry — ${newColLabel}`}
            </button>
            {editing && (
              <button onClick={cancelEdit} data-testid={tid('iq-cancel-edit')}
                className="px-4 py-3 rounded-lg text-sm font-semibold border border-slate-300 text-slate-600 bg-white hover:border-slate-400">Cancel</button>
            )}
          </div>
        )}
      </CardContent></Card>
  );
}
